import type { RouteLocationNormalizedGeneric, RouteLocationRaw } from 'vue-router'
import { resolveProjectIdFromRouteRef } from '~/entities/projects/model/resolveProjectByRouteRef'

import {
  useGetMsAnalyticsPermissionsLazyQuery,
  useGetMsCommentPermissionsLazyQuery,
  useGetMsNewsPermissionsLazyQuery,
  useGetMsProjectCounterPermissionsLazyQuery,
  useGetMsProjectCustomModulePermissionsLazyQuery,
  useGetMsProjectMemberPermissionsLazyQuery,
  useGetMsProjectMockupsPermissionsLazyQuery,
  useGetMsProjectModuleMemberPermissionsLazyQuery,
  useGetMsProjectPermissionsLazyQuery,
  useGetMsProjectProjectRequestPermissionsLazyQuery,
  useGetMsProjectSupportersPermissionsLazyQuery,
  useGetMsProjectTemplatesPermissionsLazyQuery,
  useGetMsSurveyPermissionsLazyQuery,
} from '~/shared/api'
import {
  accessCore,
  decodeJwt,
  frontendPermissions,
  isUuid,
  resolveNewsPermissionsModuleSlug,
  Resource,
  resourceAccess,
  useNotificationStore,
} from '~/shared/lib'
import { useAccountStore } from './account'

export interface ICommonContext {
  ownerId: string | null
  members: string[]
  admins: string[]
}

export const useAccessStore = defineStore(
  'access',
  () => {
    const { load: loadProjectPermissions, refetch: refetchProjectPermissions }
      = useGetMsProjectPermissionsLazyQuery(undefined, { fetchPolicy: 'no-cache' })
    const { load: loadProjectRequestsPermissions, refetch: refetchProjectRequestsPermissions }
      = useGetMsProjectProjectRequestPermissionsLazyQuery({ fetchPolicy: 'no-cache' })
    const { load: loadProjectMembersPermissions, refetch: refetchProjectMembersPermissions }
      = useGetMsProjectMemberPermissionsLazyQuery(undefined, { fetchPolicy: 'no-cache' })
    const { load: loadProjectModuleMemberPermissions, refetch: refetchProjectModuleMemberPermissions }
      = useGetMsProjectModuleMemberPermissionsLazyQuery(undefined, { fetchPolicy: 'no-cache' })
    const { load: loadProjectMockupsPermissions, refetch: refetchProjectMockupsPermissions }
      = useGetMsProjectMockupsPermissionsLazyQuery(undefined, { fetchPolicy: 'no-cache' })
    const { load: loadProjectTemplatesPermissions, refetch: refetchProjectTemplatesPermissions }
      = useGetMsProjectTemplatesPermissionsLazyQuery(undefined, { fetchPolicy: 'no-cache' })
    const { load: loadAnalyticsPermissions, refetch: refetchAnalyticsPermissions }
      = useGetMsAnalyticsPermissionsLazyQuery({ fetchPolicy: 'no-cache' })
    const { load: loadProjectSupportersPermissions, refetch: refetchProjectSupportersPermissions }
      = useGetMsProjectSupportersPermissionsLazyQuery(undefined, { fetchPolicy: 'no-cache' })
    const { load: loadNewsPermissions, refetch: refetchNewsPermissions }
      = useGetMsNewsPermissionsLazyQuery(undefined, { fetchPolicy: 'no-cache' })
    const { load: loadCommentPermissions, refetch: refetchCommentPermissions }
      = useGetMsCommentPermissionsLazyQuery({ fetchPolicy: 'no-cache' })
    const { load: loadSurveyPermissions, refetch: refetchSurveyPermissions }
      = useGetMsSurveyPermissionsLazyQuery(undefined, { fetchPolicy: 'no-cache' })
    const { load: loadProjectCounterPermissions, refetch: refetchProjectCounterPermissions }
      = useGetMsProjectCounterPermissionsLazyQuery(undefined, { fetchPolicy: 'no-cache' })
    const { load: loadProjectCustomModulePermissions, refetch: refetchProjectCustomModulePermissions }
      = useGetMsProjectCustomModulePermissionsLazyQuery(undefined, { fetchPolicy: 'no-cache' })
    const accessToken = useCookie('access_token')
    const { data: myCtx } = storeToRefs(useAccountStore())

    const ctx = ref<ICtx>({})
    const backendPermissions = ref<string[]>([])
    const backendPermissionsSet = computed(() => new Set(backendPermissions.value))

    interface ICtx {
      projectId?: string
      newsId?: string
      surveyId?: string
      moduleSlug?: string
    }

    const handlersForFetchPermissions: Record<Resource, () => Promise<string[]>> = {
      [Resource.Projects]: async () => {
        const projectId = ctx.value.projectId ?? ''

        const result = await (
          loadProjectPermissions(null, { projectId })
          || refetchProjectPermissions({ projectId })
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsProjectPermissions
        }

        return result.getMsProjectPermissions
      },
      [Resource.ProjectRequests]: async () => {
        const result = await (
          loadProjectRequestsPermissions()
          || refetchProjectRequestsPermissions()
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsProjectProjectRequestPermissions
        }

        return result.getMsProjectProjectRequestPermissions
      },
      [Resource.ProjectMembers]: async () => {
        const projectId = ctx.value.projectId ?? ''

        const result = await (
          loadProjectMembersPermissions(null, { projectId })
          || refetchProjectMembersPermissions({ projectId })
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsProjectMemberPermissions
        }

        return result.getMsProjectMemberPermissions
      },
      [Resource.ProjectModuleMember]: async () => {
        const projectId = ctx.value.projectId ?? ''

        const result = await (
          loadProjectModuleMemberPermissions(null, { projectId })
          || refetchProjectModuleMemberPermissions({ projectId })
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsProjectModuleMemberPermissions
        }

        return result.getMsProjectModuleMemberPermissions
      },
      [Resource.ProjectModuleAccess]: async () => {
        const projectId = ctx.value.projectId ?? ''

        const result = await (
          loadProjectPermissions(null, { projectId })
          || refetchProjectPermissions({ projectId })
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsProjectPermissions ?? []
        }

        return result.getMsProjectPermissions ?? []
      },
      [Resource.Mockups]: async () => {
        const projectId = ctx.value.projectId ?? ''

        const result = await (
          loadProjectMockupsPermissions(null, { projectId })
          || refetchProjectMockupsPermissions({ projectId })
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsProjectMockupsPermissions
        }

        return result.getMsProjectMockupsPermissions
      },
      [Resource.Templates]: async () => {
        const projectId = ctx.value.projectId ?? ''

        const result = await (
          loadProjectTemplatesPermissions(null, { projectId })
          || refetchProjectTemplatesPermissions({ projectId })
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsProjectTemplatesPermissions
        }

        return result.getMsProjectTemplatesPermissions
      },
      [Resource.Supporters]: async () => {
        const projectId = ctx.value.projectId ?? ''

        const result = await (
          loadProjectSupportersPermissions(null, { projectId })
          || refetchProjectSupportersPermissions({ projectId })
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsProjectSupportersPermissions
        }

        return result.getMsProjectSupportersPermissions
      },
      [Resource.Analytics]: async () => {
        const result = await (loadAnalyticsPermissions() || refetchAnalyticsPermissions())

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsAnalyticsPermissions
        }

        return result.getMsAnalyticsPermissions
      },
      [Resource.News]: async () => {
        const projectId = ctx.value.projectId ?? ''
        const newsId = ctx.value.newsId ?? ''
        const moduleSlug = ctx.value.moduleSlug

        const result = await (
          loadNewsPermissions(null, {
            projectId,
            newsId: newsId || undefined,
            moduleSlug: moduleSlug || undefined,
          })
          || refetchNewsPermissions({
            projectId,
            newsId: newsId || undefined,
            moduleSlug: moduleSlug || undefined,
          })
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsNewsPermissions
        }

        return result.getMsNewsPermissions
      },
      [Resource.Comment]: async () => {
        const result = await (loadCommentPermissions() || refetchCommentPermissions())

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsCommentPermissions
        }

        return result.getMsCommentPermissions
      },
      [Resource.Surveys]: async () => {
        const projectId = ctx.value.projectId ?? ''
        const surveyId = ctx.value.surveyId ?? ''

        const result = await (
          loadSurveyPermissions(null, { projectId, surveyId })
          || refetchSurveyPermissions({ projectId, surveyId })
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsSurveyPermissions
        }

        return result.getMsSurveyPermissions
      },
      [Resource.Counters]: async () => {
        const projectId = ctx.value.projectId ?? ''

        const result = await (
          loadProjectCounterPermissions(null, { projectId })
          || refetchProjectCounterPermissions({ projectId })
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsProjectCounterPermissions
        }

        return result.getMsProjectCounterPermissions
      },
      [Resource.ProjectCustomModule]: async () => {
        const projectId = ctx.value.projectId ?? ''

        const result = await (
          loadProjectCustomModulePermissions(null, { projectId })
          || refetchProjectCustomModulePermissions({ projectId })
        )

        if (!result) {
          return []
        }

        if (result && 'data' in result) {
          return result.data?.getMsProjectCustomModulePermissions
        }

        return result.getMsProjectCustomModulePermissions
      },
    }

    const commonContext = ref<ICommonContext>({
      ownerId: null,
      members: [],
      admins: [],
    })

    const rules: Record<string, (ctx: ICommonContext) => boolean> = {
      1: (ctx: ICommonContext) => {
        return myCtx.value.id === ctx.ownerId
      },
      2: (ctx: ICommonContext) => {
        return ctx.members?.includes(myCtx.value.id)
      },
      3: (ctx: ICommonContext) => {
        return (
          myCtx.value.id !== ctx.ownerId
          && !ctx.members?.includes(myCtx.value.id)
        )
      },
      4: (ctx: ICommonContext) => {
        return ctx.admins?.includes(myCtx.value.id)
      },
    }

    function setContext(data: Partial<ICtx>) {
      ctx.value = { ...ctx.value, ...data }
    }

    function makeContextFromResources(
      resources?: Resource[],
      to?: RouteLocationNormalizedGeneric,
      resolvedProjectId?: string,
    ) {
      const paramRef = to?.params.id as string | undefined
      const projectId = resolvedProjectId
        ?? (paramRef && isUuid(paramRef) ? paramRef : '')
      const newsId = to?.params.newsId as string | undefined
      const surveyId = to?.params.surveyId as string | undefined

      return resources?.reduce<ICtx>((acc, resource) => {
        switch (resource) {
          case Resource.Projects:
          case Resource.ProjectMembers:
          case Resource.ProjectModuleMember:
          case Resource.ProjectModuleAccess:
          case Resource.Mockups:
          case Resource.Templates:
          case Resource.Supporters:
          case Resource.Counters:
          case Resource.ProjectCustomModule:
            acc.projectId = projectId
            break
          case Resource.News:
            acc.projectId = projectId
            acc.newsId = newsId ?? ''
            if (to) {
              acc.moduleSlug = resolveNewsPermissionsModuleSlug(to, projectId)
            }
            break
          case Resource.Surveys:
            acc.projectId = projectId
            acc.surveyId = surveyId ?? ''
            break
        }

        return acc
      }, {}) ?? {}
    }

    function setCommonContext(ctx: Partial<ICommonContext>) {
      commonContext.value.members = ctx.members ?? commonContext.value.members
      commonContext.value.ownerId = ctx.ownerId ?? commonContext.value.ownerId
      commonContext.value.admins = ctx.admins ?? commonContext.value.admins
    }

    function makeContext(ctx: Partial<ICommonContext>): ICommonContext {
      return {
        ownerId: ctx.ownerId ?? null,
        members: ctx.members ?? [],
        admins: ctx.admins ?? [],
      }
    }

    function canFor(permission: string, backendPermissions: string[]) {
      const access = frontendPermissions[permission]?.access

      if (access === undefined) {
        return true
      }

      return access.some(perm => backendPermissions.includes(perm))
    }

    /** True when OPA granted project-scoped news list (module "view" capability). */
    function hasBackendNewsModuleListView(): boolean {
      return backendPermissionsSet.value.has(
        resourceAccess[Resource.News].listProject,
      )
    }

    /** True when OPA granted public news list for global digest feed. */
    function hasBackendNewsModulePublicListView(): boolean {
      return backendPermissionsSet.value.has('news:list:public')
    }

    /** True when OPA granted news create (module "data_entry" — project or own scope). */
    function hasBackendNewsModuleDataEntry(): boolean {
      return (
        backendPermissionsSet.value.has(
          resourceAccess[Resource.News].createProject,
        )
        || backendPermissionsSet.value.has(
          resourceAccess[Resource.News].createOwn,
        )
      )
    }

    /** True when OPA granted module-scoped news edit (project, own, or all). */
    function hasBackendNewsModuleEdit(): boolean {
      return (
        backendPermissionsSet.value.has(resourceAccess[Resource.News].updateProject)
        || backendPermissionsSet.value.has(resourceAccess[Resource.News].updateOwn)
        || backendPermissionsSet.value.has(resourceAccess[Resource.News].updateAll)
      )
    }

    /** True when OPA granted module-scoped news delete. */
    function hasBackendNewsModuleDelete(): boolean {
      return (
        backendPermissionsSet.value.has(resourceAccess[Resource.News].deleteProject)
        || backendPermissionsSet.value.has(resourceAccess[Resource.News].deleteAll)
      )
    }

    function hasBackendNewsModuleFullAccess(): boolean {
      return (
        backendPermissionsSet.value.has(resourceAccess[Resource.News].createAll)
        && backendPermissionsSet.value.has(resourceAccess[Resource.News].updateAll)
      )
    }

    function hasBackendNewsModuleEditAllRecords(): boolean {
      return (
        hasBackendNewsModuleFullAccess()
        || backendPermissionsSet.value.has(resourceAccess[Resource.News].updateAll)
      )
    }

    function can(permission: string, ctx?: Partial<ICommonContext> | null, isBackend?: boolean) {
      if (isBackend) {
        const access = frontendPermissions[permission]?.access

        if (access === undefined) {
          return true
        }

        return access.some(perm => backendPermissionsSet.value.has(perm))
      }

      if (!accessToken.value) {
        return false
      }

      const jwtResult = decodeJwt<{ realm_access: { roles: Array<string> } }>(
        accessToken.value,
      )
      const roles = new Set(jwtResult?.realm_access.roles ?? [])

      if (!roles.size) {
        return false
      }

      const approvedRoles = accessCore.roles.filter(item =>
        roles.has(item.name),
      )

      if (!approvedRoles.length) {
        return false
      }

      const accessPermission = accessCore.permissions.find(
        item => item.name === permission,
      )

      if (!accessPermission) {
        return true
      }

      return approvedRoles.some((item) => {
        const hasRole = accessPermission.roles.includes(item.id)

        if (accessPermission.rules?.length) {
          return (
            hasRole
            && accessPermission.rules.some(ruleId =>
              rules[ruleId]?.(ctx ? makeContext(ctx) : commonContext.value),
            )
          )
        }

        return hasRole
      })
    }

    function canNavigateTo(
      permission: string,
      to: RouteLocationRaw | undefined | null,
      options?: any,
    ) {
      if (!can(permission)) {
        useNotificationStore().notify({
          message: 'У вас недостаточно прав',
          type: 'warning',
        })
        return
      }

      navigateTo(to, options)
    }

    async function fetchBackPermissions(to: RouteLocationNormalizedGeneric) {
      const name = to.name as string

      if (!name) {
        backendPermissions.value = []

        return
      }

      const permissions = frontendPermissions[name]
      const paramRef = to?.params.id as string | undefined
      let resolvedProjectId = paramRef ?? ''

      if (paramRef && !isUuid(paramRef)) {
        resolvedProjectId = await resolveProjectIdFromRouteRef(paramRef)
      }

      const context = makeContextFromResources(permissions?.resources, to, resolvedProjectId)

      setContext(context)

      if (!permissions) {
        backendPermissions.value = []

        return
      }

      const resources = permissions.resources

      if (!resources) {
        backendPermissions.value = []

        return
      }

      const data = await Promise.all(
        resources.map(async (resource) => {
          try {
            return await handlersForFetchPermissions[resource]()
          }
          catch (error) {
            console.error(`[access] failed to load permissions for ${resource}`, error)
            return []
          }
        }),
      )

      backendPermissions.value = data.flat()
    }

    return {
      can,
      canFor,
      canNavigateTo,
      setCommonContext,
      setContext,
      commonContext,
      fetchBackPermissions,
      hasBackendNewsModuleListView,
      hasBackendNewsModulePublicListView,
      hasBackendNewsModuleDataEntry,
      hasBackendNewsModuleEdit,
      hasBackendNewsModuleDelete,
      hasBackendNewsModuleFullAccess,
      hasBackendNewsModuleEditAllRecords,
    }
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.cookies({
        httpOnly: false,
        sameSite: 'strict',
      }),
    },
  },
)
